'use client';

import Link from 'next/link';
import * as Icon from '@/ui/Icon';
import { IOrganization, IUser } from '@/types';

import * as Styled from './styled';
import { Row } from '@/ui/primatives';
import OrganizationNavTabs, { OrganizationTabType } from './OrganizationNavTabs';
import UserNavTabs from './UserNavTabs';

const UserInfo: React.FC<{ user: IUser }> = ({ user }) => {
  return (
    <Styled.NamespaceInfo>
      <Styled.NamespacePath>
        <Row gap={10}>
          <Link className="hover-link" href={`/${user.username}`}>
            {user.name} ({user.username})
          </Link>
        </Row>
      </Styled.NamespacePath>
      <Styled.NamespaceActions>
        <Link href={`mailto:${user.email}`}>
          <Row gap={5} alignItems="center">
            <Icon.Envelope size="1em" />
            {user.email}
          </Row>
        </Link>
      </Styled.NamespaceActions>
    </Styled.NamespaceInfo>
  );
};

type BaseNavTabProps = {
  currentUser?: IUser;
  activeTab?: OrganizationTabType;
};

type UserNavTabProps = BaseNavTabProps & {
  user: IUser;
  organization?: never;
};

type OrganizationNavTabProps = BaseNavTabProps & {
  user?: never;
  organization: IOrganization;
};

export const NamespaceNavTabs: React.FC<UserNavTabProps | OrganizationNavTabProps> = ({
  currentUser,
  user,
  organization,
  activeTab,
}) => {
  return user ? (
    <UserNavTabs user={user} currentUser={currentUser} />
  ) : (
    <OrganizationNavTabs
      organization={organization}
      currentUser={currentUser}
      activeTab={activeTab as OrganizationTabType}
    />
  );
};

const OrganizationInfo: React.FC<{ organization: IOrganization }> = ({ organization }) => {
  return (
    <Styled.NamespaceInfo>
      <Styled.NamespacePath>
        <Row gap={10}>
          <Link className="hover-link" href={`/${organization.name}`}>
            {organization.display_name} ({organization.name})
          </Link>
        </Row>
      </Styled.NamespacePath>
      <Styled.NamespaceActions></Styled.NamespaceActions>
    </Styled.NamespaceInfo>
  );
};

const NamespaceHeader: React.FC<{
  currentUser?: IUser;
  user?: IUser;
  organization?: IOrganization;
  activeTab?: OrganizationTabType;
}> = ({ currentUser, user, organization, activeTab = 'repositories' }) => (
  <Styled.NamespaceHeader>
    {user && <UserInfo user={user} />}
    {organization && <OrganizationInfo organization={organization} />}
    {user && <UserNavTabs user={user} currentUser={currentUser} />}
    {organization && (
      <OrganizationNavTabs
        organization={organization}
        currentUser={currentUser}
        activeTab={activeTab as OrganizationTabType}
      />
    )}
  </Styled.NamespaceHeader>
);

export default NamespaceHeader;
